import { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Alert,
} from "reactstrap";
import { Field, FieldArray } from "formik";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import FeatherIcon from "feather-icons-react";
import PropTypes from "prop-types";

const MetaFields = ({ values, setFieldValue, errors, touched, toggleGroup, openGroups }) => {
  console.log({ values: values.meta.parking })
  const [previousValues, setPreviousValues] = useState(null);
  const [isAllNA, setIsAllNA] = useState(false);

  const getNestedError = (path) => {
    const pathParts = path.split(".");
    const touchedValue = pathParts.reduce((obj, key) => obj?.[key], touched);
    const errorValue = pathParts.reduce((obj, key) => obj?.[key], errors);

    if (touchedValue && errorValue && typeof errorValue === 'string') {
      return errorValue;
    }
    return null;
  };

  const hasMetaErrors = () => {
    if (!errors.meta || !touched.meta) return false;

    if (typeof errors.meta === 'string') return true;

    const checkNestedErrors = (obj, touchedObj) => {
      if (!obj || !touchedObj) return false;

      for (const key in obj) {
        if (touchedObj[key]) {
          if (typeof obj[key] === 'string') return true;
          if (typeof obj[key] === 'object' && checkNestedErrors(obj[key], touchedObj[key])) {
            return true;
          }
        }
      }
      return false;
    };

    return checkNestedErrors(errors.meta, touched.meta);
  };

  const hasUtilityErrors = () => {
    if (!errors.meta?.accounts || !touched.meta?.accounts) return false;

    return Object.keys(errors.meta.accounts).some(key =>
      touched.meta.accounts[key] && typeof errors.meta.accounts[key] === 'string'
    );
  };

  const hasGeneralErrors = () => {
    if (!errors.meta || !touched.meta) return false;

    const sections = ['parking', 'storage', 'fobs', 'keys', 'occupants'];
    return sections.some(section => {
      if (errors.meta[section] && touched.meta[section]) {
        if (typeof errors.meta[section] === 'string') return true;
        if (Array.isArray(errors.meta[section])) {
          return errors.meta[section].some((item, index) =>
            touched.meta[section][index] &&
            Object.keys(item || {}).some(key =>
              touched.meta[section][index][key] && typeof item[key] === 'string'
            )
          );
        }
      }
      return false;
    });
  };

  const parkingTypeOptions = [
    { value: "Surface Staff", label: "Surface Staff" },
    { value: "Underground", label: "Underground" },
  ];

  const storageTypeOptions = [
    { value: "Surface Staff", label: "Surface Staff" },
    { value: "Underground", label: "Underground" },
    { value: "Outside", label: "Outside" },
  ];

  const fobTypeOptions = [
    { value: "Underground", label: "Underground" },
    { value: "Surface Stall", label: "Surface Stall" },
  ];

  const numberOptions = [
    ...Array.from({ length: 100 }, (_, i) => ({
      value: i.toString(),
      label: i.toString(),
    })),
  ];

  const maxItemsConfig = {
    parking: 5,
    storage: 5,
    fobs: 7,
    keys: 5,
    occupants: 10,
  };

  const selectStyles = (errorPath) => ({
    control: (base) => ({
      ...base,
      borderColor: getNestedError(errorPath) ? "#dc3545" : base.borderColor,
      "&:hover": {
        borderColor: getNestedError(errorPath) ? "#dc3545" : base["&:hover"].borderColor,
      },
    }),
  });

  const isAccountsNA = () => {
    return ["heat", "water", "electricity"].every(
      (key) => values.meta?.accounts?.[key] === "N/A"
    );
  };

  const handleAccountsCheckbox = (checked) => {
    ["heat", "water", "electricity"].forEach((key) => {
      setFieldValue(`meta.accounts.${key}`, checked ? "N/A" : "");
    });
  };

  const isParkingNA = () => {
    return (
      values.meta?.parking?.length === 1 &&
      values.meta.parking[0]?.type === "N/A" &&
      values.meta.parking[0]?.stall_no === "N/A" &&
      values.meta.parking[0]?.parking_pass === false
    );
  };

  const handleParkingCheckbox = (checked) => {
    setFieldValue("meta.parking", checked ? [{ type: "N/A", stall_no: "N/A", parking_pass: false }] : []);
  };

  const isStorageNA = () => {
    return (
      values.meta?.storage?.length === 1 &&
      values.meta.storage[0]?.type === "N/A" &&
      values.meta.storage[0]?.stall_no === "N/A"
    );
  };

  const handleStorageCheckbox = (checked) => {
    setFieldValue("meta.storage", checked ? [{ type: "N/A", stall_no: "N/A" }] : []);
  };

  const isFobsNA = () => {
    return (
      values.meta?.fobs?.length === 1 &&
      values.meta.fobs[0]?.stall_no === "N/A" &&
      values.meta.fobs[0]?.type === "N/A" &&
      values.meta.fobs[0]?.name === "N/A"
    );
  };

  const handleFobsCheckbox = (checked) => {
    setFieldValue("meta.fobs", checked ? [{ stall_no: "N/A", type: "N/A", name: "N/A" }] : []);
  };

  const isKeysNA = () => {
    return (
      values.meta?.keys?.length === 1 &&
      values.meta.keys[0]?.main_entry === "N/A" &&
      values.meta.keys[0]?.suite === "N/A" &&
      values.meta.keys[0]?.mailbox === "N/A" &&
      values.meta.keys[0]?.storage === "N/A"
    );
  };

  const handleKeysCheckbox = (checked) => {
    setFieldValue(
      "meta.keys",
      checked ? [{ main_entry: "N/A", suite: "N/A", mailbox: "N/A", storage: "N/A" }] : []
    );
  };

  const isOccupantsNA = () => {
    return (
      values.meta?.occupants?.length === 1 &&
      values.meta.occupants[0]?.name === "N/A" &&
      values.meta.occupants[0]?.phone === "N/A" &&
      values.meta.occupants[0]?.email === "N/A" &&
      values.meta.occupants[0]?.age === 0
    );
  };

  const handleOccupantsCheckbox = (checked) => {
    setFieldValue(
      "meta.occupants",
      checked ? [{ name: "N/A", phone: "N/A", email: "N/A", age: 0 }] : []
    );
  };

  const isAllSectionsNA = () => {
    return (
      isAccountsNA() &&
      isParkingNA() &&
      isStorageNA() &&
      isFobsNA() &&
      isKeysNA() &&
      isOccupantsNA()
    );
  };

  const handleSetAllNA = () => {
    if (!isAllNA) {
      setPreviousValues({
        accounts: { ...values.meta?.accounts },
        parking: [...(values.meta?.parking || [])],
        storage: [...(values.meta?.storage || [])],
        fobs: [...(values.meta?.fobs || [])],
        keys: [...(values.meta?.keys || [])],
        occupants: [...(values.meta?.occupants || [])],
      });
      setFieldValue("meta.parking", [{ type: "N/A", stall_no: "N/A", parking_pass: false }]);
      setFieldValue("meta.storage", [{ type: "N/A", stall_no: "N/A" }]);
      setFieldValue("meta.fobs", [{ stall_no: "N/A", type: "N/A", name: "N/A" }]);
      setFieldValue("meta.keys", [{ main_entry: "N/A", suite: "N/A", mailbox: "N/A", storage: "N/A" }]);
      setFieldValue("meta.occupants", [{ name: "N/A", phone: "N/A", email: "N/A", age: 0 }]);
      ["heat", "water", "electricity"].forEach((key) => {
        setFieldValue(`meta.accounts.${key}`, "N/A");
      });
      setIsAllNA(true);
    } else if (previousValues) {
      setFieldValue("meta.accounts", { ...previousValues.accounts });
      setFieldValue("meta.parking", [...previousValues.parking]);
      setFieldValue("meta.storage", [...previousValues.storage]);
      setFieldValue("meta.fobs", [...previousValues.fobs]);
      setFieldValue("meta.keys", [...previousValues.keys]);
      setFieldValue("meta.occupants", [...previousValues.occupants]);
      setIsAllNA(false);
      setPreviousValues(null);
    }
  };

  const renderAccountsSection = () => (
    <Row>
      {["heat", "water", "electricity"].map((key, index) => (
        <Col md={4} key={key}>
          <FormGroup>
            {index === 0 && (
              <Label className="d-flex align-items-center justify-content-between w-100" htmlFor={`meta.accounts.${key}`}>
                <span>{key.charAt(0).toUpperCase() + key.slice(1)} Provider/Account # *</span>
                <div className="d-flex align-items-center">
                  <Label className="form-label mb-0 me-2" htmlFor="na-checkbox-accounts">
                    Not Applicable?
                  </Label>
                  <Input
                    type="checkbox"
                    id="na-checkbox-accounts"
                    className="form-check-input"
                    checked={isAccountsNA()}
                    onChange={(e) => handleAccountsCheckbox(e.target.checked)}
                  />
                </div>
              </Label>
            )}
            {index !== 0 && (
              <Label htmlFor={`meta.accounts.${key}`}>
                {key.charAt(0).toUpperCase() + key.slice(1)} Provider/Account # *
              </Label>
            )}
            <Field
              name={`meta.accounts.${key}`}
              id={`meta.accounts.${key}`}
              type="text"
              as={Input}
              invalid={!!getNestedError(`meta.accounts.${key}`)}
              onChange={(e) => setFieldValue(`meta.accounts.${key}`, e.target.value)}
              placeholder="Enter provider/account #"
            />
            <FormFeedback>{getNestedError(`meta.accounts.${key}`)}</FormFeedback>
          </FormGroup>
        </Col>
      ))}
    </Row>
  );

  const renderSlotArray = (sectionName, displayName, typeOptions, maxEntries = Infinity) => (
    <div className="mb-3">
      <FieldArray name={`meta.${sectionName}`}>
        {({ push, remove }) => (
          <>
            {(!values.meta?.[sectionName] || values.meta[sectionName].length === 0) &&
              getNestedError(`meta.${sectionName}`) && (
                <Alert color="danger" className="mb-2">
                  {getNestedError(`meta.${sectionName}`)}
                </Alert>
              )}
            {values.meta?.[sectionName]?.map((_, index) => (
              <Row key={index} className="mb-2">
                <Col md={5}>
                  <FormGroup>
                    {index === 0 && (
                      <Label className="d-flex align-items-center justify-content-between w-100" htmlFor={`meta.${sectionName}[${index}].type`}>
                        <span>{displayName} Type *</span>
                        <div className="d-flex align-items-center">
                          <Label className="form-label mb-0 me-2" htmlFor={`na-checkbox-${sectionName}`}>
                            Not Applicable?
                          </Label>
                          <Input
                            type="checkbox"
                            id={`na-checkbox-${sectionName}`}
                            className="form-check-input"
                            checked={sectionName === "parking" ? isParkingNA() : isStorageNA()}
                            onChange={(e) =>
                              sectionName === "parking"
                                ? handleParkingCheckbox(e.target.checked)
                                : handleStorageCheckbox(e.target.checked)
                            }
                          />
                        </div>
                      </Label>
                    )}
                    {index !== 0 && (
                      <Label htmlFor={`meta.${sectionName}[${index}].type`}>{displayName} Type *</Label>
                    )}
                    <CreatableSelect
                      id={`meta.${sectionName}[${index}].type`}
                      name={`meta.${sectionName}[${index}].type`}
                      options={typeOptions}
                      value={
                        values.meta[sectionName][index]?.type
                          ? {
                            value: values.meta[sectionName][index].type,
                            label: values.meta[sectionName][index].type,
                          }
                          : null
                      }
                      onChange={(selectedOption) =>
                        setFieldValue(
                          `meta.${sectionName}[${index}].type`,
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      placeholder={`Select or enter ${displayName.toLowerCase()} type...`}
                      styles={selectStyles(`meta.${sectionName}[${index}].type`)}
                      className={getNestedError(`meta.${sectionName}[${index}].type`) ? "is-invalid" : ""}
                    />
                    {getNestedError(`meta.${sectionName}[${index}].type`) && (
                      <div className="invalid-feedback d-block">
                        {getNestedError(`meta.${sectionName}[${index}].type`)}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <Label className="d-flex align-items-center justify-content-between w-100" htmlFor={`meta.${sectionName}[${index}].stall_no`}>
                      <span>Stall Type *</span>
                      {sectionName === "parking" && (
                        <div className="d-flex align-items-center">
                          <Label className="form-label mb-0 me-2" htmlFor={`meta.${sectionName}[${index}].parking_pass`}>
                            Parking Pass?
                          </Label>
                          <Field
                            type="checkbox"
                            id={`meta.${sectionName}[${index}].parking_pass`}
                            name={`meta.${sectionName}[${index}].parking_pass`}
                            className="form-check-input"
                            onChange={(e) =>
                              setFieldValue(`meta.${sectionName}[${index}].parking_pass`, e.target.checked)
                            }
                          />
                        </div>
                      )}
                    </Label>
                    <Field
                      name={`meta.${sectionName}[${index}].stall_no`}
                      id={`meta.${sectionName}[${index}].stall_no`}
                      type="text"
                      as={Input}
                      invalid={!!getNestedError(`meta.${sectionName}[${index}].stall_no`)}
                      onChange={(e) =>
                        setFieldValue(`meta.${sectionName}[${index}].stall_no`, e.target.value)
                      }
                      placeholder="Enter number"
                    />
                    <FormFeedback>{getNestedError(`meta.${sectionName}[${index}].stall_no`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <div className="d-flex align-items-center mt-4">
                    {values.meta[sectionName].length < maxEntries && (
                      <Button
                        color="primary"
                        size="sm"
                        className="me-1"
                        onClick={() => {
                          push(
                            sectionName === "parking"
                              ? { type: "", stall_no: "", parking_pass: false }
                              : { type: "", stall_no: "" }
                          );
                          toggleGroup(`meta.${sectionName}[${values.meta[sectionName].length}]`, true);
                        }}
                      >
                        <FeatherIcon icon="plus" size="16" />
                      </Button>
                    )}
                    <Button color="danger" size="sm" onClick={() => remove(index)}>
                      <FeatherIcon icon="x" size="16" />
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}
            {(!values.meta?.[sectionName] || values.meta[sectionName].length === 0) && (
              <div className="text-center mb-4">
                <Button
                  color={getNestedError(`meta.${sectionName}`) ? "danger" : "secondary"}
                  outline
                  onClick={() => {
                    push(
                      sectionName === "parking"
                        ? { type: "", stall_no: "", parking_pass: false }
                        : { type: "", stall_no: "" }
                    );
                    toggleGroup(`meta.${sectionName}[0]`, true);
                  }}
                >
                  Add {displayName} Slot
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );

  const renderFobArray = () => (
    <div className="mb-3">
      <FieldArray name="meta.fobs">
        {({ push, remove }) => (
          <>
            {(!values.meta?.fobs || values.meta.fobs.length === 0) && getNestedError("meta.fobs") && (
              <Alert color="danger" className="mb-2">
                {getNestedError("meta.fobs")}
              </Alert>
            )}
            {(values.meta?.fobs || []).map((_, index) => (
              <Row key={index} className="mb-2">
                <Col md={3}>
                  <FormGroup>
                    {index === 0 && (
                      <Label className="d-flex align-items-center justify-content-between w-100" htmlFor={`meta.fobs[${index}].stall_no`}>
                        <span>FOB # *</span>
                        <div className="d-flex align-items-center">
                          <Label className="form-label mb-0 me-2" htmlFor="na-checkbox-fobs">
                            Not Applicable?
                          </Label>
                          <Input
                            type="checkbox"
                            id="na-checkbox-fobs"
                            className="form-check-input"
                            checked={isFobsNA()}
                            onChange={(e) => handleFobsCheckbox(e.target.checked)}
                          />
                        </div>
                      </Label>
                    )}
                    {index !== 0 && (
                      <Label htmlFor={`meta.fobs[${index}].stall_no`}>FOB No. *</Label>
                    )}
                    <Field
                      name={`meta.fobs[${index}].stall_no`}
                      id={`meta.fobs[${index}].stall_no`}
                      type="text"
                      as={Input}
                      invalid={!!getNestedError(`meta.fobs[${index}].stall_no`)}
                      onChange={(e) =>
                        setFieldValue(`meta.fobs[${index}].stall_no`, e.target.value)
                      }
                      placeholder="Enter FOB #"
                    />
                    <FormFeedback>{getNestedError(`meta.fobs[${index}].stall_no`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label htmlFor={`meta.fobs[${index}].type`}>Type *</Label>
                    <Select
                      id={`meta.fobs[${index}].type`}
                      name={`meta.fobs[${index}].type`}
                      options={fobTypeOptions}
                      value={
                        values.meta.fobs[index]?.type
                          ? {
                            value: values.meta.fobs[index].type,
                            label: values.meta.fobs[index].type,
                          }
                          : null
                      }
                      onChange={(selectedOption) =>
                        setFieldValue(`meta.fobs[${index}].type`, selectedOption ? selectedOption.value : "")
                      }
                      placeholder="Select FOB type..."
                      styles={selectStyles(`meta.fobs[${index}].type`)}
                      className={getNestedError(`meta.fobs[${index}].type`) ? "is-invalid" : ""}
                    />
                    {getNestedError(`meta.fobs[${index}].type`) && (
                      <div className="invalid-feedback d-block">
                        {getNestedError(`meta.fobs[${index}].type`)}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label htmlFor={`meta.fobs[${index}].name`}>Name *</Label>
                    <Field
                      name={`meta.fobs[${index}].name`}
                      id={`meta.fobs[${index}].name`}
                      type="text"
                      as={Input}
                      invalid={!!getNestedError(`meta.fobs[${index}].name`)}
                      onChange={(e) =>
                        setFieldValue(`meta.fobs[${index}].name`, e.target.value)
                      }
                      placeholder="Enter name"
                    />
                    <FormFeedback>{getNestedError(`meta.fobs[${index}].name`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <div className="d-flex align-items-center mt-4">
                    {(values.meta?.fobs || []).length < maxItemsConfig.fobs && (
                      <Button
                        color="primary"
                        size="sm"
                        className="me-1"
                        onClick={() => {
                          push({ stall_no: "", type: "", name: "" });
                          toggleGroup(`meta.fobs[${(values.meta?.fobs || []).length}]`, true);
                        }}
                      >
                        <FeatherIcon icon="plus" size="16" />
                      </Button>
                    )}
                    <Button color="danger" size="sm" onClick={() => remove(index)}>
                      <FeatherIcon icon="x" size="16" />
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}
            {(!values.meta?.fobs || values.meta.fobs.length === 0) && (
              <div className="text-center mb-4">
                <Button
                  color={getNestedError("meta.fobs") ? "danger" : "secondary"}
                  outline
                  onClick={() => {
                    push({ stall_no: "", type: "", name: "" });
                    toggleGroup("meta.fobs[0]", true);
                  }}
                >
                  Add FOB
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );

  const renderKeysSection = () => (
    <div className="mb-3">
      <FieldArray name="meta.keys">
        {({ push, remove }) => (
          <>
            {(!values.meta?.keys || values.meta.keys.length === 0) && getNestedError("meta.keys") && (
              <Alert color="danger" className="mb-2">
                {getNestedError("meta.keys")}
              </Alert>
            )}
            {(values.meta?.keys || []).map((_, index) => (
              <Row key={index} className="mb-2">
                <Col md={3}>
                  <FormGroup>
                    {index === 0 && (
                      <Label className="d-flex align-items-center justify-content-between w-100" htmlFor={`meta.keys[${index}].main_entry`}>
                        <span>Main Entry Door Key *</span>
                        <div className="d-flex align-items-center">
                          <Label className="form-label mb-0 me-2" htmlFor="na-checkbox-keys">
                            Not Applicable?
                          </Label>
                          <Input
                            type="checkbox"
                            id="na-checkbox-keys"
                            className="form-check-input"
                            checked={isKeysNA()}
                            onChange={(e) => handleKeysCheckbox(e.target.checked)}
                          />
                        </div>
                      </Label>
                    )}
                    {index !== 0 && (
                      <Label htmlFor={`meta.keys[${index}].main_entry`}>Main Entry Door Key *</Label>
                    )}
                    <Field name={`meta.keys[${index}].main_entry`}>
                      {({ field }) => (
                        <CreatableSelect
                          id={`meta.keys[${index}].main_entry`}
                          name={`meta.keys[${index}].main_entry`}
                          options={numberOptions}
                          value={
                            numberOptions.find((option) => option.value === field.value) || {
                              value: field.value,
                              label: field.value,
                            }
                          }
                          onChange={(option) =>
                            setFieldValue(`meta.keys[${index}].main_entry`, option ? option.value : "")
                          }
                          onInputChange={(input, { action }) => {
                            if (action === "input-change" && input) {
                              setFieldValue(`meta.keys[${index}].main_entry`, input);
                            }
                          }}
                          placeholder="Select or enter key #"
                          styles={selectStyles(`meta.keys[${index}].main_entry`)}
                          className={getNestedError(`meta.keys[${index}].main_entry`) ? "is-invalid" : ""}
                        />
                      )}
                    </Field>
                    <FormFeedback>{getNestedError(`meta.keys[${index}].main_entry`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label htmlFor={`meta.keys[${index}].suite`}>Suite Key *</Label>
                    <Field name={`meta.keys[${index}].suite`}>
                      {({ field }) => (
                        <CreatableSelect
                          id={`meta.keys[${index}].suite`}
                          name={`meta.keys[${index}].suite`}
                          options={numberOptions}
                          value={
                            numberOptions.find((option) => option.value === field.value) || {
                              value: field.value,
                              label: field.value,
                            }
                          }
                          onChange={(option) =>
                            setFieldValue(`meta.keys[${index}].suite`, option ? option.value : "")
                          }
                          onInputChange={(input, { action }) => {
                            if (action === "input-change" && input) {
                              setFieldValue(`meta.keys[${index}].suite`, input);
                            }
                          }}
                          placeholder="Select or enter key #"
                          styles={selectStyles(`meta.keys[${index}].suite`)}
                          className={getNestedError(`meta.keys[${index}].suite`) ? "is-invalid" : ""}
                        />
                      )}
                    </Field>
                    <FormFeedback>{getNestedError(`meta.keys[${index}].suite`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label htmlFor={`meta.keys[${index}].mailbox`}>Mail Box Key *</Label>
                    <Field name={`meta.keys[${index}].mailbox`}>
                      {({ field }) => (
                        <CreatableSelect
                          id={`meta.keys[${index}].mailbox`}
                          name={`meta.keys[${index}].mailbox`}
                          options={numberOptions}
                          value={
                            numberOptions.find((option) => option.value === field.value) || {
                              value: field.value,
                              label: field.value,
                            }
                          }
                          onChange={(option) =>
                            setFieldValue(`meta.keys[${index}].mailbox`, option ? option.value : "")
                          }
                          onInputChange={(input, { action }) => {
                            if (action === "input-change" && input) {
                              setFieldValue(`meta.keys[${index}].mailbox`, input);
                            }
                          }}
                          placeholder="Select or enter key #"
                          styles={selectStyles(`meta.keys[${index}].mailbox`)}
                          className={getNestedError(`meta.keys[${index}].mailbox`) ? "is-invalid" : ""}
                        />
                      )}
                    </Field>
                    <FormFeedback>{getNestedError(`meta.keys[${index}].mailbox`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label htmlFor={`meta.keys[${index}].storage`}>Storage Key *</Label>
                    <Field name={`meta.keys[${index}].storage`}>
                      {({ field }) => (
                        <CreatableSelect
                          id={`meta.keys[${index}].storage`}
                          name={`meta.keys[${index}].storage`}
                          options={numberOptions}
                          value={
                            numberOptions.find((option) => option.value === field.value) || {
                              value: field.value,
                              label: field.value,
                            }
                          }
                          onChange={(option) =>
                            setFieldValue(`meta.keys[${index}].storage`, option ? option.value : "")
                          }
                          onInputChange={(input, { action }) => {
                            if (action === "input-change" && input) {
                              setFieldValue(`meta.keys[${index}].storage`, input);
                            }
                          }}
                          placeholder="Select or enter key #"
                          styles={selectStyles(`meta.keys[${index}].storage`)}
                          className={getNestedError(`meta.keys[${index}].storage`) ? "is-invalid" : ""}
                        />
                      )}
                    </Field>
                    <FormFeedback>{getNestedError(`meta.keys[${index}].storage`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <div className="d-flex align-items-center mt-4">
                    {(values.meta?.keys || []).length < maxItemsConfig.keys && (
                      <Button
                        color="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          push({ main_entry: "", suite: "", mailbox: "", storage: "" });
                          toggleGroup(`meta.keys[${(values.meta?.keys || []).length}]`, true);
                        }}
                        disabled={(values.meta?.keys || []).length >= maxItemsConfig.keys}
                      >
                        <FeatherIcon icon="plus" size="16" />
                      </Button>
                    )}
                    <Button color="danger" className="ms-1" size="sm" onClick={() => remove(index)}>
                      <FeatherIcon icon="x" size="16" />
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}
            {(!values.meta?.keys || values.meta.keys.length === 0) && (
              <div className="text-center mb-4">
                <Button
                  color={getNestedError("meta.keys") ? "danger" : "secondary"}
                  outline
                  onClick={() => {
                    push({ main_entry: "", suite: "", mailbox: "", storage: "" });
                    toggleGroup("meta.keys[0]", true);
                  }}
                >
                  Add Keys
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );

  const renderOccupantsSection = () => (
    <div className="mb-3">
      <FieldArray name="meta.occupants">
        {({ push, remove }) => (
          <>
            {(!values.meta?.occupants || values.meta.occupants.length === 0) &&
              getNestedError("meta.occupants") && (
                <Alert color="danger" className="mb-2">
                  {getNestedError("meta.occupants")}
                </Alert>
              )}
            {(values.meta?.occupants || []).map((_, index) => (
              <Row key={index} className="mb-4">
                <Col md={4}>
                  <FormGroup>
                    {index === 0 && (
                      <Label className="d-flex align-items-center justify-content-between w-100" htmlFor={`meta.occupants[${index}].name`}>
                        <span>Occupant Name *</span>
                        <div className="d-flex align-items-center">
                          <Label className="form-label mb-0 me-2" htmlFor="na-checkbox-occupants">
                            Not Applicable?
                          </Label>
                          <Input
                            type="checkbox"
                            id="na-checkbox-occupants"
                            className="form-check-input"
                            checked={isOccupantsNA()}
                            onChange={(e) => handleOccupantsCheckbox(e.target.checked)}
                          />
                        </div>
                      </Label>
                    )}
                    {index !== 0 && (
                      <Label htmlFor={`meta.occupants[${index}].name`}>Occupant Name *</Label>
                    )}
                    <Field
                      name={`meta.occupants[${index}].name`}
                      id={`meta.occupants[${index}].name`}
                      type="text"
                      as={Input}
                      invalid={!!getNestedError(`meta.occupants[${index}].name`)}
                      onChange={(e) =>
                        setFieldValue(`meta.occupants[${index}].name`, e.target.value)
                      }
                      placeholder="Enter name"
                    />
                    <FormFeedback>{getNestedError(`meta.occupants[${index}].name`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label htmlFor={`meta.occupants[${index}].phone`}>Phone *</Label>
                    <Field
                      name={`meta.occupants[${index}].phone`}
                      id={`meta.occupants[${index}].phone`}
                      type="text"
                      as={Input}
                      invalid={!!getNestedError(`meta.occupants[${index}].phone`)}
                      onChange={(e) =>
                        setFieldValue(`meta.occupants[${index}].phone`, e.target.value)
                      }
                      placeholder="Enter phone"
                    />
                    <FormFeedback>{getNestedError(`meta.occupants[${index}].phone`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label htmlFor={`meta.occupants[${index}].email`}>Email *</Label>
                    <Field
                      name={`meta.occupants[${index}].email`}
                      id={`meta.occupants[${index}].email`}
                      type="text"
                      as={Input}
                      invalid={!!getNestedError(`meta.occupants[${index}].email`)}
                      onChange={(e) =>
                        setFieldValue(`meta.occupants[${index}].email`, e.target.value)
                      }
                      placeholder="Enter email"
                    />
                    <FormFeedback>{getNestedError(`meta.occupants[${index}].email`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label htmlFor={`meta.occupants[${index}].age`}>Age</Label>
                    <Field
                      name={`meta.occupants[${index}].age`}
                      id={`meta.occupants[${index}].age`}
                      type="number"
                      as={Input}
                      invalid={!!getNestedError(`meta.occupants[${index}].age`)}
                      onChange={(e) =>
                        setFieldValue(
                          `meta.occupants[${index}].age`,
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      placeholder="Enter age"
                    />
                    <FormFeedback>{getNestedError(`meta.occupants[${index}].age`)}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={1}>
                  <div className="d-flex align-items-center mt-4">
                    <Button
                      color="primary"
                      size="sm"
                      className="ms-auto"
                      onClick={() => {
                        push({ name: "", phone: "", email: "", age: null });
                        toggleGroup(`meta.occupants[${(values.meta?.occupants || []).length}]`, true);
                      }}
                    >
                      <FeatherIcon icon="plus" size="16" />
                    </Button>
                    <Button color="danger" size="sm" className="ms-1" onClick={() => remove(index)}>
                      <FeatherIcon icon="x" size="16" />
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}
            {(!values.meta?.occupants || values.meta.occupants.length === 0) && (
              <div className="text-center mb-4">
                <Button
                  color={getNestedError("meta.occupants") ? "danger" : "secondary"}
                  outline
                  onClick={() => {
                    push({ name: "", phone: "", email: "", age: null });
                    toggleGroup("meta.occupants[0]", true);
                  }}
                >
                  Add Occupant
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );

  return (
    <>
      {hasMetaErrors() && (
        <Alert color="danger" className="mb-3">
          Please fix the errors in the form below.
        </Alert>
      )}
      <Card className="mb-3">
        <CardHeader className="py-3 text-center">
          <Button
            color={hasUtilityErrors() ? "danger" : "primary"}
            outline
            className="fw-bold px-4 py-2 rounded-pill"
            onClick={() => toggleGroup("meta")}
          >
            <FeatherIcon icon="zap" className="me-2" size={16} />
            Utility Information
          </Button>
        </CardHeader>
        <Collapse isOpen={openGroups["meta"]}>
          <CardBody>{renderAccountsSection()}</CardBody>
        </Collapse>
      </Card>
      <Card className="mb-3">
        <CardHeader className="py-3 text-center">
          <Button
            color={hasGeneralErrors() ? "danger" : "primary"}
            outline
            className="fw-bold px-4 py-2 rounded-pill"
            onClick={() => toggleGroup("general")}
          >
            <FeatherIcon icon="home" className="me-2" size={16} />
            General Information
          </Button>
        </CardHeader>
        <Collapse isOpen={openGroups["general"]}>
          <CardBody>
            <div className="mb-3 d-flex justify-content-center">
              <Button
                color={isAllNA ? "success" : "warning"}
                className="d-flex align-items-center gap-1"
                style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
                onClick={handleSetAllNA}
              >
                <FeatherIcon icon="x-circle" size="16" />
                {isAllNA ? "Restore Previous Values" : "Set All as N/A"}
              </Button>
            </div>
            <Row>
              <Col md={6}>{renderSlotArray("parking", "Parking", parkingTypeOptions, maxItemsConfig.parking)}</Col>
              <Col md={6}>{renderSlotArray("storage", "Storage", storageTypeOptions, maxItemsConfig.storage)}</Col>
            </Row>
            <Row>
              <Col md={12}>{renderOccupantsSection()}</Col>
              <Col md={12}>{renderFobArray()}</Col>
            </Row>
            <Row>{renderKeysSection()}</Row>
          </CardBody>
        </Collapse>
      </Card>
    </>
  );
};

MetaFields.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  toggleGroup: PropTypes.func.isRequired,
  openGroups: PropTypes.object.isRequired,
};

export { MetaFields };